// ============================================================
// ChooseBillCaseModal.tsx — the client-wide Activity tab's "Invoice" action.
// A bill belongs to ONE case, and the client tab spans all of them, so the
// Invoice tile asks which case first; picking one opens that case's Create
// bill (RaiseBillModal) right over the client page — the case page is not
// opened (ClientSearchView loads the case's billing and hosts the modal).
// The cases listed are the client's cases this user can see (server
// audience-filtered), labelled name + THE reference like every other picker.
// ============================================================
import React from 'react'
import ModalFrame from '../shared/ModalFrame'
import { BIcon, B_SECONDARY, NOTE } from './billingUi'

export interface BillCaseOption { id: string; name: string; ref: string }

const ChooseBillCaseModal: React.FC<{
  cases: BillCaseOption[] | null
  clientName: string
  onPick: (caseId: string) => void
  onClose: () => void
}> = ({ cases, clientName, onPick, onClose }) => (
  <ModalFrame onClose={onClose} size="sm" portal eyebrow="Invoice" eyebrowIcon={<BIcon name="bill" size={14} />}
    title="Which case is this bill for?" subtitle={`A bill is raised on one case. Pick one of ${clientName || 'this client'}’s cases to raise the bill on.`}
    footerRight={<button type="button" className={B_SECONDARY} onClick={onClose}>Cancel</button>}>
    {cases === null
      ? <p className={`${NOTE} m-0`}>Loading cases…</p>
      : cases.length === 0
        ? <p className={`${NOTE} m-0`}>This client has no cases you can raise a bill on.</p>
        : (
          <ul className="m-0 p-0 list-none grid gap-2" aria-label="Cases">
            {cases.map((c) => (
              <li key={c.id}>
                <button type="button" onClick={() => onPick(c.id)}
                  className="w-full flex items-center gap-3 text-left rounded-[12px] border border-casevo-border bg-casevo-surface px-3 py-[10px] hover:border-casevo-mid hover:bg-casevo-input transition-colors focus:outline-none casevo-focus">
                  <span className="min-w-0 flex-1">
                    <b className="block text-[13.5px] font-semibold text-casevo-forest truncate">{c.name}</b>
                    <small className="block text-[11.5px] font-mono text-casevo-text-soft">{c.ref}</small>
                  </span>
                  <BIcon name="chevr" size={12} className="text-casevo-text-soft shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        )}
  </ModalFrame>
)

export default ChooseBillCaseModal
